import {Injectable, computed, inject, signal} from '@angular/core';
import {firstValueFrom, Subscription} from 'rxjs';
import {
  ChatRepositoryPort,
  DeliveryTarget,
  PaymentRetryDetails,
  PaymentRetryResponse,
  ReportContext,
  ReportSubmission
} from '@domain/ports';
import { UserRepositoryPort } from '@domain/ports';
import { ChatRealtimePort, RealtimeConnectionState } from '@domain/ports';
import { Chat, ChatDetail, ChatSummary } from '@domain/models';
import { Message, PaymentRetryStatus, PaymentStatus, ReportStatusUpdate, ValidationStatus, ValidationUpdate } from '@domain/models';
import { UploadResult } from '@domain/ports';
import { User } from '@domain/models';
import {AuthStore} from './auth.store';

export type ReportSubmissionResult =
  | { ok: true; submission: ReportSubmission }
  | { ok: false; error: unknown };

@Injectable({providedIn: 'root'})
export class ChatStore {
  private chatRepo = inject(ChatRepositoryPort);
  private userRepo = inject(UserRepositoryPort);
  private realtime = inject(ChatRealtimePort);
  private auth = inject(AuthStore);

  private _chats = signal<Chat[]>([]);
  private _selectedChatId = signal<string | number | null>(null);
  private _otherUsers = signal<Map<string, User>>(new Map());
  private _filter = signal<string>('');
  private _contacts = signal<User[]>([]);
  private _loading = signal<boolean>(false);
  private _chatLoading = signal<boolean>(false);
  private _connection = signal<RealtimeConnectionState>('disconnected');
  private _typing = signal<boolean>(false);

  readonly loading = this._loading.asReadonly();
  readonly chatLoading = this._chatLoading.asReadonly();
  readonly connection = this._connection.asReadonly();
  readonly typing = this._typing.asReadonly();

  readonly chats = computed(() => {
    const term = this._filter().trim().toLowerCase();
    const list = this._chats();
    if (!term) return list;
    return list.filter(c => {
      const other = this.otherUserOf(c);
      return other?.fullName.toLowerCase().includes(term);
    });
  });

  readonly selectedChat = computed(() => {
    const id = this._selectedChatId();
    if (id == null) return null;
    return this._chats().find(c => c.id === id) ?? null;
  });

  readonly otherUserForSelected = computed(() => {
    const chat = this.selectedChat();
    if (!chat) return null;
    return this.otherUserOf(chat);
  });

  readonly contacts = this._contacts.asReadonly();

  private incomingSub: Subscription | null = null;
  private validationSub: Subscription | null = null;
  private reportStatusSub: Subscription | null = null;
  private connectionSub: Subscription | null = null;
  private typingSub: Subscription | null = null;
  private readonly loadedChatIds = new Set<string | number>();
  private readonly loadingChatIds = new Set<string | number>();
  private readonly startingChatByContactId = new Map<string, Promise<void>>();

  async load(): Promise<void> {
    const me = this.auth.currentUser();
    if (!me) return;

    this._loading.set(true);
    try {
      const [summaries, users] = await Promise.all([
        firstValueFrom(this.chatRepo.list()),
        firstValueFrom(this.userRepo.list())
      ]);
      const map = new Map<string, User>();
      users.forEach(u => map.set(u.id, u));
      this._otherUsers.set(map);
      this._contacts.set(users.filter(u => u.id !== me.id));
      this._chats.set(summaries.map(summary => this.summaryToChat(summary)));

      this.incomingSub?.unsubscribe();
      this.incomingSub = this.realtime.incoming$.subscribe(msg => this.applyIncoming(msg));
      this.validationSub?.unsubscribe();
      this.validationSub = this.realtime.validationUpdates$.subscribe(
        update => this.applyValidation(update)
      );
      this.reportStatusSub?.unsubscribe();
      this.reportStatusSub = this.realtime.reportStatusUpdates$?.subscribe(
        update => this.applyReportStatus(update)
      ) ?? null;
      this.connectionSub?.unsubscribe();
      this.connectionSub = this.realtime.connection$.subscribe(s => this._connection.set(s));
      this.typingSub?.unsubscribe();
      this.typingSub = this.realtime.typing$.subscribe(t => this._typing.set(t));
      this.realtime.connect();
    } finally {
      this._loading.set(false);
    }
  }

  selectChat(id: string | number | null): void {
    this._selectedChatId.set(id);
    if (id == null || this.loadedChatIds.has(id)) {
      this._chatLoading.set(false);
      return;
    }
    this._chatLoading.set(true);
    void this.loadChatDetail(id);
  }

  getReportContext(messageId: string): Promise<ReportContext> {
    const chat = this.selectedChat();
    if (!chat) return Promise.reject(new Error('No hay chat seleccionado'));
    return firstValueFrom(this.chatRepo.getReportContext(chat.id, messageId));
  }

  async submitReport(messageId: string, agentCode: string,
    deliveryTarget: DeliveryTarget): Promise<ReportSubmissionResult> {
    try {
      const chat = this.selectedChat();
      if (!chat) throw new Error('No hay chat seleccionado');
      const submission = await firstValueFrom(
        this.chatRepo.submitReport(chat.id, messageId, agentCode, deliveryTarget)
      );
      this._chats.set(this._chats().map(candidate => candidate.id !== chat.id ? candidate : {
        ...candidate,
        messages: candidate.messages.map(message => message.id !== submission.id ? message : {
          ...message,
          text: submission.text,
          reportStatus: message.reportStatus === 'COMPLETED' || message.reportStatus === 'FAILED'
            ? message.reportStatus : submission.reportStatus
        })
      }));
      return { ok: true, submission };
    } catch (error) {
      return { ok: false, error };
    }
  }

  async submitPaymentRetry(botMessageId: string,
    details: PaymentRetryDetails): Promise<
      {ok: true; response: PaymentRetryResponse} | {ok: false; error: unknown}
    > {
    try {
      const chat = this.selectedChat();
      if (!chat) throw new Error('No hay chat seleccionado');

      const targetMessage = chat.messages.find(message =>
        (message.messageId ?? message.id) === botMessageId
      );
      if (!targetMessage || targetMessage.paymentRetryStatus !== 'AVAILABLE') {
        throw new Error('El mensaje ya no requiere reintento de pago');
      }

      const clientMessageId = this.generateClientMessageId();
      const response = await firstValueFrom(
        this.chatRepo.submitPaymentRetry(chat.id, botMessageId, {
          ...details,
          clientMessageId
        })
      );
      const terminalStatus: PaymentRetryStatus = response.paymentRetryStatus;
      this._chats.set(this._chats().map(candidate => candidate.id !== chat.id ? candidate : {
        ...candidate,
        messages: candidate.messages.map(message =>
          message.id === response.id || message.messageId === response.id
            ? {...message, paymentRetryStatus: terminalStatus}
            : message
        )
      }));
      if (response.userMessage) {
        this.addIncomingToState({
          ...response.userMessage,
          userId: response.userMessage.userId ?? this.auth.currentUser()?.id ?? '',
          clientMessageId: response.userMessage.clientMessageId
            ?? clientMessageId,
          status: response.userMessage.status ?? 'sent'
        }, true);
      }
      return {ok: true, response};
    } catch (error) {
      return {ok: false, error};
    }
  }

  async refreshSelectedChat(): Promise<boolean> {
    const chat = this.selectedChat();
    if (!chat || this.loadingChatIds.has(chat.id)) return false;
    return this.loadChatDetail(chat.id, true);
  }

  setFilter(value: string): void {
    this._filter.set(value);
  }

  otherUserOf(chat: ChatSummary): User | null {
    const me = this.auth.currentUser();
    if (!me) return null;
    const otherId = chat.userId === me.id ? chat.userId2 : chat.userId;
    return this._otherUsers().get(otherId) ?? null;
  }

  async sendMessage(text: string): Promise<void> {
    const chat = this.selectedChat();
    const me = this.auth.currentUser();
    if (!chat || !me || !text.trim()) return;
    const clientMessageId = this.generateClientMessageId();
    this.realtime.sendMessage(chat.id, clientMessageId, text);
  }

  addLocalImagePreview(clientMessageId: string, previewUrl: string, caption?: string): void {
    const chat = this.selectedChat();
    const me = this.auth.currentUser();
    if (!chat || !me) return;
    this.addIncomingToState({
      id: clientMessageId,
      chatId: chat.id,
      userId: me.id,
      text: previewUrl,
      sender: 'user',
      createdAt: Date.now(),
      kind: 'IMAGE',
      clientMessageId,
      mediaUrl: previewUrl,
      localPreviewUrl: previewUrl,
      caption,
      status: 'sending'
    });
  }

  async completeImageUpload(
    clientMessageId: string,
    upload: UploadResult
  ): Promise<void> {
    const chat = this.selectedChat();
    const me = this.auth.currentUser();
    if (!chat || !me) return;
    this._chats.set(this._chats().map(existingChat => {
      if (existingChat.id !== chat.id) return existingChat;
      return {
        ...existingChat,
        messages: existingChat.messages.map(message =>
          message.clientMessageId === clientMessageId
            ? { ...message, remoteMediaUrl: upload.url }
            : message
        )
      };
    }));
    this.realtime.sendImage(chat.id, clientMessageId, upload.url);
  }

  markImageUploadFailed(clientMessageId: string, reason: string): void {
    this._chats.set(this._chats().map(chat => {
      let changed = false;
      const messages: Message[] = chat.messages.map(message => {
        if (message.clientMessageId !== clientMessageId) return message;
        changed = true;
        return {
          ...message,
          status: 'failed',
          validation: 'ERROR',
          validationReason: reason
        };
      });
      return changed ? { ...chat, messages } : chat;
    }));
  }

  async startChatWith(contactId: string): Promise<void> {
    const me = this.auth.currentUser();
    if (!me) return;

    const existing = this._chats().find(
      c => (c.userId === me.id && c.userId2 === contactId) ||
        (c.userId2 === me.id && c.userId === contactId)
    );
    if (existing) {
      this.selectChat(existing.id);
      return;
    }

    const inFlight = this.startingChatByContactId.get(contactId);
    if (inFlight) return inFlight;

    const request = this.createOrSelectChat(me.id, contactId)
      .finally(() => this.startingChatByContactId.delete(contactId));
    this.startingChatByContactId.set(contactId, request);
    return request;
  }

  private async createOrSelectChat(userId: string, contactId: string): Promise<void> {
    const detail = await firstValueFrom(this.chatRepo.create({
      userId,
      userId2: contactId
    }));

    const chat = this.normalizeHistoryChat(detail);
    this.upsertChat(chat);
    this.loadedChatIds.add(chat.id);
    await this.refreshChatSummaries();

    if (!this._chats().some(candidate => candidate.id === chat.id)) {
      this.upsertChat(chat);
    }

    this.loadedChatIds.add(chat.id);
    this.selectChat(chat.id);
  }

  private async applyIncoming(msg: Message): Promise<void> {
    if (msg.sender === 'bot' && msg.semanticType === 'AGENT_REPLY') {
      this.applyImageBotStatus(msg);
    }
    this.addIncomingToState(msg);
  }

  private addIncomingToState(msg: Message, sortByCreatedAt = false): void {
    const chats = this._chats().map(c => {
      if (c.id !== msg.chatId) return c;
      const messages = this.upsertMessage(c.messages, msg, sortByCreatedAt);
      return this.withUpdatedSummary({...c, messages}, msg);
    });
    this._chats.set(this.sortChats(chats));
  }

  private normalizeHistoryChat(chat: ChatDetail): Chat {
    const messages = chat.messages.map(message => this.normalizeHistoryMessage(message));
    const last = messages.at(-1);
    return {
      ...chat,
      lastActivityAt: last?.createdAt ?? chat.createdAt,
      lastMessagePreview: last
        ? (last.kind === 'IMAGE' ? last.caption || 'Imagen' : last.text)
        : null,
      messages
    };
  }

  private normalizeHistoryMessage(message: Message): Message {
    const normalizedIdentity = message.sender === 'user' && !message.clientMessageId && message.messageId
      ? {
          ...message,
          clientMessageId: message.messageId,
          messageId: message.id,
          status: message.status ?? 'sent' as const
        }
      : message;
    const normalized = this.applyPersistedProcessingStatus(normalizedIdentity);
    const persistedPaymentStatus = this.asPaymentStatus(normalized.validation);
    if (persistedPaymentStatus) {
      const validation = this.paymentValidationStatus(persistedPaymentStatus);
      return {
        ...normalized,
        paymentStatus: persistedPaymentStatus,
        validation,
        status: validation === 'ERROR' ? 'failed' : 'done'
      };
    }
    return this.applyPersistedValidationStatus(normalized);
  }

  private applyPersistedProcessingStatus(message: Message): Message {
    if (message.sender !== 'user') return message;
    switch (message.processingStatus) {
      case 'ACKNOWLEDGED':
        return {...message, status: 'sent'};
      case 'PROCESSING':
        return {
          ...message,
          status: 'processing',
          validation: message.kind === 'IMAGE' && message.validation == null
            ? 'PENDING'
            : message.validation
        };
      default:
        return message;
    }
  }

  private applyPersistedValidationStatus(message: Message): Message {
    if (message.kind !== 'IMAGE') return message;
    switch (message.validation) {
      case 'PENDING':
        return {...message, status: 'processing'};
      case 'OK':
      case 'REJECTED':
        return {...message, status: 'done'};
      case 'ERROR':
        return {...message, status: 'failed'};
      default:
        return message;
    }
  }

  private sortChats(chats: Chat[]): Chat[] {
    return [...chats].sort((a, b) => {
      return b.lastActivityAt - a.lastActivityAt;
    });
  }

  private async refreshChatSummaries(): Promise<void> {
    const summaries = await firstValueFrom(this.chatRepo.list());
    const currentById = new Map(this._chats().map(chat => [chat.id, chat]));
    this._chats.set(summaries.map(summary => {
      const existing = currentById.get(summary.id);
      return existing ? {...existing, ...summary} : this.summaryToChat(summary);
    }));
  }

  private upsertChat(chat: Chat): void {
    const current = this._chats();
    const existingIndex = current.findIndex(candidate => candidate.id === chat.id);
    if (existingIndex < 0) {
      this._chats.set(this.sortChats([chat, ...current]));
      return;
    }

    const next = [...current];
    next[existingIndex] = chat;
    this._chats.set(this.sortChats(next));
  }

  private upsertMessage(
    messages: Message[],
    incoming: Message,
    sortByCreatedAt = false
  ): Message[] {
    const index = messages.findIndex(existing => this.isSameMessage(existing, incoming));
    const message = index < 0 ? incoming : this.mergeMessage(messages[index], incoming);
    const next = index < 0
      ? messages
      : messages.filter((_, messageIndex) => messageIndex !== index);
    if (!sortByCreatedAt || message.createdAt == null) return [...next, message];

    const insertAt = next.findIndex(existing =>
      existing.createdAt != null && existing.createdAt > message.createdAt!
    );
    if (insertAt < 0) return [...next, message];
    return [...next.slice(0, insertAt), message, ...next.slice(insertAt)];
  }

  private isSameMessage(a: Message, b: Message): boolean {
    if (a.clientMessageId && b.clientMessageId) return a.clientMessageId === b.clientMessageId;
    if (a.messageId && b.messageId) return a.messageId === b.messageId;
    if (this.isSameOptimisticUserMessage(a, b)) return true;
    return a.id === b.id;
  }

  private isSameOptimisticUserMessage(current: Message, incoming: Message): boolean {
    return current.sender === 'user'
      && incoming.sender === 'user'
      && current.status === 'sending'
      && !current.messageId
      && !!incoming.messageId
      && current.chatId === incoming.chatId
      && current.userId === incoming.userId
      && current.kind === incoming.kind
      && current.text === incoming.text;
  }

  private mergeMessage(current: Message, incoming: Message): Message {
    const incomingRemoteMediaUrl = incoming.remoteMediaUrl
      ?? (
        current.localPreviewUrl
        && incoming.mediaUrl
        && !incoming.mediaUrl.startsWith('blob:')
          ? incoming.mediaUrl
          : current.remoteMediaUrl
      );
    const mergedMediaUrl = current.localPreviewUrl
      && current.mediaUrl?.startsWith('blob:')
      && incoming.mediaUrl
      && !incoming.mediaUrl.startsWith('blob:')
      ? current.mediaUrl
      : incoming.mediaUrl ?? current.mediaUrl;
    return {
      ...current,
      ...incoming,
      id: incoming.messageId ?? incoming.id ?? current.id,
      text: incoming.text || current.text,
      mediaUrl: mergedMediaUrl,
      localPreviewUrl: incoming.localPreviewUrl ?? current.localPreviewUrl,
      remoteMediaUrl: incomingRemoteMediaUrl,
      caption: incoming.caption ?? current.caption,
      validation: incoming.validation ?? current.validation,
      paymentStatus: incoming.paymentStatus ?? current.paymentStatus,
      validationReason: incoming.validationReason ?? current.validationReason
    };
  }

  private applyValidation(update: ValidationUpdate): void {
    this._chats.set(this._chats().map(c => {
      let changed = false;
      const messages = c.messages.map(m => {
        const matchesMessageId = update.messageId != null && m.messageId === update.messageId;
        const matchesClientId = update.clientMessageId != null && m.clientMessageId === update.clientMessageId;
        const matchesParentId = update.parentMessageId != null && m.messageId === update.parentMessageId;
        if (!matchesMessageId && !matchesClientId && !matchesParentId) return m;
        changed = true;
        const status: Message['status'] = update.status === 'PENDING'
          ? 'processing'
          : update.status === 'ERROR'
            ? 'failed'
            : 'done';
        return {
          ...m,
          status,
          createdAt: update.createdAt ?? m.createdAt,
          validation: update.status,
          paymentStatus: update.paymentStatus ?? m.paymentStatus,
          validationReason: update.reason
        };
      });
      return changed ? {...c, messages} : c;
    }));
  }

  private applyReportStatus(update: ReportStatusUpdate): void {
    this._chats.set(this._chats().map(chat => chat.id !== update.chatId ? chat : {
      ...chat,
      messages: chat.messages.map(message => message.id !== update.messageId ? message : {
        ...message,
        reportStatus: update.reportStatus
      })
    }));
  }

  reset(): void {
    this._chats.set([]);
    this._selectedChatId.set(null);
    this._otherUsers.set(new Map());
    this._filter.set('');
    this._contacts.set([]);
    this._loading.set(false);
    this._chatLoading.set(false);
    this.loadedChatIds.clear();
    this.loadingChatIds.clear();
    this.startingChatByContactId.clear();
    this.incomingSub?.unsubscribe();
    this.incomingSub = null;
    this.validationSub?.unsubscribe();
    this.validationSub = null;
    this.reportStatusSub?.unsubscribe();
    this.reportStatusSub = null;
    this.connectionSub?.unsubscribe();
    this.connectionSub = null;
    this.typingSub?.unsubscribe();
    this.typingSub = null;
    this._connection.set('disconnected');
    this._typing.set(false);
    this.realtime.disconnect();
  }

  private async loadChatDetail(chatId: string | number, force = false): Promise<boolean> {
    if ((!force && this.loadedChatIds.has(chatId)) || this.loadingChatIds.has(chatId)) return false;
    this.loadingChatIds.add(chatId);
    if (this._selectedChatId() === chatId) this._chatLoading.set(true);
    try {
      const detail = await firstValueFrom(this.chatRepo.getById(chatId));
      if (!detail) return false;
      const normalized = this.normalizeHistoryChat(detail);
      this._chats.set(this._chats().map(chat => {
        if (chat.id !== chatId) return chat;
        const optimistic = chat.messages.filter(message =>
          message.status === 'sending'
          && !normalized.messages.some(history => this.isSameMessage(history, message))
        );
        return {
          ...chat,
          ...normalized,
          lastActivityAt: chat.lastActivityAt,
          lastMessagePreview: chat.lastMessagePreview,
          messages: [...normalized.messages, ...optimistic]
        };
      }));
      this.loadedChatIds.add(chatId);
      return true;
    } catch (error) {
      console.error(`Unable to load chat ${chatId}`, error);
      return false;
    } finally {
      this.loadingChatIds.delete(chatId);
      if (this._selectedChatId() === chatId) this._chatLoading.set(false);
    }
  }

  private summaryToChat(summary: ChatSummary): Chat {
    return {...summary, messages: []};
  }

  private withUpdatedSummary(chat: Chat, message: Message): Chat {
    if (!message.text && !message.mediaUrl) return chat;
    const lastActivityAt = message.createdAt ?? Date.now();
    if (chat.lastActivityAt != null && lastActivityAt < chat.lastActivityAt) return chat;
    const preview = message.kind === 'IMAGE' ? message.caption || 'Imagen' : message.text;
    return {
      ...chat,
      lastMessagePreview: preview,
      lastActivityAt
    };
  }

  private applyImageBotStatus(message: Message): void {
    if (!message.parentClientMessageId && !message.parentMessageId) return;
    this._chats.set(this._chats().map(chat => {
      if (chat.id !== message.chatId) return chat;
      let matched = false;
      const messages = chat.messages.map(candidate => {
        const sameClientId = message.parentClientMessageId
          && candidate.clientMessageId === message.parentClientMessageId;
        const sameMessageId = message.parentMessageId
          && candidate.messageId === message.parentMessageId;
        if (candidate.kind !== 'IMAGE' || (!sameClientId && !sameMessageId)) return candidate;
        matched = true;
        return {...candidate, validation: 'PENDING' as const, status: 'processing' as const};
      });
      return matched ? {...chat, messages} : chat;
    }));
  }

  private asPaymentStatus(value: unknown): PaymentStatus | null {
    const statuses: PaymentStatus[] = [
      'VALIDATED',
      'DUPLICATED',
      'NOT_FOUND',
      'DATE_EXPIRED',
      'NON_PAYMENT_IMAGE',
      'CHANNEL_NOT_ALLOWED',
      'MANUAL_REVIEW_REQUIRED',
      'USER_CONFIRMATION_REQUIRED',
      'ERROR_AGENT',
      'ERROR_PROCESSOR'
    ];
    return statuses.includes(value as PaymentStatus) ? value as PaymentStatus : null;
  }

  private paymentValidationStatus(status: PaymentStatus): ValidationStatus {
    if (status === 'VALIDATED') return 'OK';
    if (status === 'ERROR_AGENT' || status === 'ERROR_PROCESSOR') return 'ERROR';
    return 'REJECTED';
  }

  private generateClientMessageId(): string {
    return crypto.randomUUID();
  }
}
